import { API_BASE_PATH } from '../apiConfig';

export type LoadState = 'idle' | 'loading' | 'ready' | 'error';
export type PricingPlan = 'full' | 'light' | 'free';

const MAX_CODEBASE_ZIP_SIZE_BYTES = 35 * 1024 * 1024;

export const formatFileSize = (size: number): string => {
  return size < 1024 * 1024
    ? `${Math.max(1, Math.round(size / 1024))} KB`
    : `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export const getCodebaseZipValidationError = (file: File): string => {
  if (!file.name.toLowerCase().endsWith('.zip')) {
    return 'Select a .zip archive.';
  }

  if (file.size > MAX_CODEBASE_ZIP_SIZE_BYTES) {
    return 'The ZIP file is too large. Maximum size is 35 MB.';
  }

  return '';
};

export const SKILL_LABELS: Record<string, string> = {
  pashov: 'Pashov',
  'sc-auditor': 'SC Auditor',
  quillshield: 'QuillShield',
  cyfrin: 'Cyfrin',
  'trail-of-bits': 'Trail of Bits',
  'trail-of-bits-supply-chain': 'Trail of bits',
  'trail-of-bits-static-analysis': 'Trail of bits',
  'semgrep-code-security': 'Semgrep',
  forefy: 'Forefy'
};

export const compactScopePaths = (scopePaths: string[]): string[] => {
  const uniqueScopes = Array.from(new Set(scopePaths
    .map((scopePath) => scopePath.trim().replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, ''))
    .filter(Boolean)
  )).sort((left, right) => left.localeCompare(right));

  return uniqueScopes.filter((scopePath, index) => {
    return !uniqueScopes.some((candidate, candidateIndex) => {
      return candidateIndex !== index && scopePath.startsWith(`${candidate}/`);
    });
  });
};

export const isScopePathIncluded = (scopePaths: string[], scopePath: string): boolean => {
  return scopePaths.includes(scopePath)
    || scopePaths.some((candidate) => scopePath.startsWith(`${candidate}/`));
};

export const formatModelLabel = (model: string): string => {
  // opencode model ids are provider-qualified, for example openrouter/z-ai/glm-5.3.
  const trimmed = model.trim().slice(model.trim().lastIndexOf('/') + 1);
  const normalized = trimmed.toLowerCase();

  if (normalized === 'gpt-daybreak-blue-latest') {
    return 'Daybreak Blue';
  }

  if (normalized.startsWith('glm-')) {
    return `GLM-${trimmed.slice(4)}`;
  }

  if (normalized.startsWith('gpt-')) {
    const [version = '', ...variantParts] = trimmed.slice(4).split('-').filter(Boolean);
    const variant = variantParts
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

    return variant ? `GPT-${version} ${variant}` : `GPT-${version}`;
  }

  if (normalized.startsWith('claude-')) {
    return normalized
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  return trimmed;
};

export const formatLabel = (value: string, labels: Record<string, string>): string => {
  if (labels[value]) {
    return labels[value];
  }

  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getErrorMessage = (payload: unknown, fallbackStatus: number): string => {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const { error } = payload as { error?: unknown };

    if (typeof error === 'string' && error.trim()) {
      return error;
    }
  }

  return `Request failed with status ${fallbackStatus}`;
};

class ApiRequestError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.payload = payload;
  }
}

export const requestJson = async <ResponseType,>(path: string, init?: RequestInit): Promise<ResponseType> => {
  const headers = new Headers(init?.headers);

  if (typeof init?.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE_PATH}${path}`, {
    ...init,
    headers,
    credentials: 'include'
  });
  const payload = await response.json().catch(() => undefined) as unknown;

  if (!response.ok) {
    throw new ApiRequestError(getErrorMessage(payload, response.status), response.status, payload);
  }

  return payload as ResponseType;
};
